// A line whose number is still at another carrier.
//
// WHY THIS IS ON THE LINE PAGE. Kevin, 2026-08-20, on Christopher Sloan: "looks like he is in
// another system." His line is built on PBX-01 with an extension and no number, and the headline
// reads "No number" — so this is where an operator asks "why not, and when?".
//
// WHAT VERSO'S JOB IS. thinQ has no create-port-in endpoint; every port call it documents takes a
// portinId that already exists. Kevin: "I don't see a create port-in, this might be something we can
// only do from the Commio UI." So the order is raised by a human in the carrier's portal and this
// panel is the record, the checklist and the watcher — never the submitter. The one carrier call
// here is a READ: has it landed yet.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// D1 hands back "2026-08-20 18:12:03"; only the day means anything to somebody chasing a port.
const datePart = (v) => (v ? String(v).slice(0, 10) : null);

const fmt = (e164) => {
  const d = String(e164 || '').replace(/[^0-9]/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (e164 || '—');
};

// Our word for where it is, in the operator's language rather than the carrier's.
const SAYS = {
  draft: 'Recorded here. Not filed with the carrier yet.',
  submitted: 'Filed with the carrier. Waiting for them to accept it.',
  pending: 'Accepted by the carrier. Waiting for the port date.',
  completed: 'The number is ours.',
  rejected: 'The carrier refused it.',
  cancelled: 'Cancelled.',
};

export default function LinePortIn({ line, canWrite, onChanged }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    number: '', losing_carrier: '', account_number: '', account_pin: '', authorized_name: '',
    requested_foc: '', commio_portin_id: '',
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const portsQ = useQuery({
    queryKey: ['port-requests', 'line', String(line.id)],
    queryFn: () => api.get('/port-requests', { subscriber_id: line.subscriberId }),
  });
  const mine = (portsQ.data?.requests ?? []).filter((p) => p.lineId === line.id);
  // Newest first — the list comes back that way, and an old rejected attempt should not be the
  // one on screen.
  const port = mine[0] ?? null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['port-requests', 'line', String(line.id)] });
    onChanged?.();
  };

  const create = useMutation({
    mutationFn: () => api.post('/port-requests', {
      subscriber_id: line.subscriberId,
      line_id: line.id,
      numbers: [form.number],
      losing_carrier: form.losing_carrier.trim() || null,
      account_number: form.account_number.trim() || null,
      account_pin: form.account_pin.trim() || null,
      authorized_name: form.authorized_name.trim() || null,
      requested_foc: form.requested_foc || null,
      location_id: line.locationId ?? null,
    }),
    onSuccess: async (made) => {
      // The ticket number, if the operator already has it from the portal. Recorded as a second
      // step because the create call has no field for it that the carrier would honour anyway.
      if (form.commio_portin_id.trim()) {
        await api.patch(`/port-requests/${made.id}`, {
          commio_portin_id: form.commio_portin_id.trim(), status: 'submitted',
        }).catch(() => {});
      }
      setAdding(false);
      invalidate();
    },
  });

  // The only carrier call on this panel, and it is a read. It also refreshes the carrier's own
  // status, FOC date and ticket onto the record, so none of those is hand-maintained.
  const check = useMutation({
    mutationFn: () => api.post(`/port-requests/${port.id}/check`, {}),
    onSuccess: invalidate,
  });

  // CUTOVER, IN TWO CLICKS ON PURPOSE. The first previews — the API dry-runs by default and this
  // asks for it explicitly anyway — and the second commits. One button that swapped a live
  // customer's number on a single click is not a button, it is a hazard.
  const [plan, setPlan] = useState(null);
  const [done, setDone] = useState(null);
  const preview = useMutation({
    mutationFn: () => api.post(`/port-requests/${port.id}/complete`, { dry_run: true }),
    onSuccess: (d) => setPlan(d),
  });
  const cutover = useMutation({
    mutationFn: () => api.post(`/port-requests/${port.id}/complete`, { dry_run: false }),
    onSuccess: (d) => { setPlan(null); setDone(d); invalidate(); },
  });

  // THE BRIDGE. Kevin, 2026-08-20: "we can add a temp did and use call forwarding on the real did to
  // forward to the temp... everything else can be set up normally while we are awaiting the
  // completion of the port." A stand-in number turns a line that cannot be built into an ordinary
  // line with a working phone, and the port becomes a swap with a date on it.
  const invQ = useQuery({
    queryKey: ['dids', 'inventory'],
    queryFn: () => api.get('/dids', { unassigned: 1 }),
    // Only when there is a bridge to set up. Every other line has no use for the inventory list.
    enabled: Boolean(port && !port.tempDidId && !line.number && port.status !== 'completed'),
  });
  const inventory = Array.isArray(invQ.data) ? invQ.data : (invQ.data?.dids ?? []);

  const assignTemp = useMutation({
    mutationFn: (didId) => api.post(`/port-requests/${port.id}/temp-did`, { did_id: Number(didId) }),
    onSuccess: invalidate,
  });
  const releaseTemp = useMutation({
    mutationFn: () => api.delete(`/port-requests/${port.id}/temp-did`),
    onSuccess: invalidate,
  });

  if (portsQ.isLoading) return null;

  // NOTHING TO SAY on an ordinary line — which is almost every line. The panel is mounted for all of
  // them so that it does not vanish the moment a stand-in number is assigned, which is exactly when
  // the port needs watching; it stays silent unless there is something true to report.
  if (!port && line.number) return null;

  if (!port) {
    if (!canWrite) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-900">No number yet</h2>
        {!adding ? (
          <>
            <p className="mt-1 text-sm text-gray-600">
              If the customer is keeping a number from another carrier, record the port here so this
              line reads as waiting rather than unfinished.
            </p>
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Record a port-in
            </button>
          </>
        ) : (
          <>
            <label className="mt-3 block text-sm font-medium text-gray-700" htmlFor="pi-number">
              Number being ported in
            </label>
            <input
              id="pi-number"
              value={form.number}
              onChange={set('number')}
              placeholder="(720) 893-2452"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />

            {/* WHAT REJECTIONS ARE MADE OF. The losing carrier checks these against their own
                record, and a mismatch comes back days later as a refusal that does not say which
                field was wrong. None of them is required here — a port waiting on a PIN the
                customer has to phone in for is still worth recording. */}
            <p className="mb-2 mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
              What the old carrier checks
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['losing_carrier', 'Current carrier'],
                ['account_number', 'Account number'],
                ['account_pin', 'Account PIN'],
                ['authorized_name', 'Name on the account'],
              ].map(([k, ph]) => (
                <input
                  key={k}
                  aria-label={ph}
                  value={form[k]}
                  onChange={set(k)}
                  placeholder={ph}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700" htmlFor="pi-foc">
                  Requested port date
                </label>
                <input
                  id="pi-foc"
                  type="date"
                  value={form.requested_foc}
                  onChange={set('requested_foc')}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700" htmlFor="pi-ticket">
                  Commio port ID <span className="font-normal text-gray-400">(if filed)</span>
                </label>
                <input
                  id="pi-ticket"
                  value={form.commio_portin_id}
                  onChange={set('commio_portin_id')}
                  placeholder="645943"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            {create.isError && (
              <p className="mt-2 text-sm text-red-600">
                {create.error?.body?.error || create.error?.message || 'Could not record it'}
              </p>
            )}

            {/* Said next to the button, because an operator who thinks this filed the port will
                wait for a date that is never coming. */}
            <p className="mt-3 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700">
              This records the port in Verso. It does <strong>not</strong> file it with the carrier —
              raise the order in the Commio portal. Verso then watches the carrier and marks the
              number as arrived on its own.
            </p>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={form.number.replace(/[^0-9]/g, '').length < 10 || create.isPending}
                onClick={() => create.mutate()}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
              >
                {create.isPending ? 'Saving…' : 'Record it'}
              </button>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  const open = ['draft', 'submitted', 'pending'].includes(port.status);
  const checked = check.data;
  const usingTemp = port.tempDidId != null
    && String(line.number || '').replace(/[^0-9]/g, '') === String(port.tempE164 || '').replace(/[^0-9]/g, '');

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      {/* ONE CARD, AND THE VERB IN THE CORNER. Kevin, 2026-08-20: "need to clean up the copy and keep
          it simple. just the port card is sufficient with the temp number listed... move the check
          port button to the top right of the card."

          WHAT WENT: a bordered sub-panel under the fields carrying three paragraphs — the stand-in
          explained in a sentence, a line about whether the old carrier's forwarding was confirmed, a
          confirm button, a rule about when handing the number back is safe, and the consequence of
          doing it early. All true; none of it something an operator reads twice. The stand-in is a
          fact about this port, so it is a field beside the others. */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">
          Porting in {port.numbers.map((n) => fmt(n.e164)).join(', ')}
        </h2>
        <div className="flex items-center gap-3">
          {checked && (
            <span className="text-xs text-gray-600">
              {/* FOUR OUTCOMES NOW, AND NO TWO OF THEM ARE THE SAME.
                  An error means we could not ask; saying "not yet" instead is how a completed port
                  sits unnoticed for a week.
                  And "not with the carrier yet" was being shown for Sloan's number, which the
                  carrier has a DID record AND an accepted order with a date for. It means "not ours
                  yet", and on a number the carrier plainly holds a record of it read as though the
                  order had gone missing. The two are only the same thing before the port is filed. */}
              {checked.landed > 0
                ? `${checked.landed} arrived.`
                : checked.unknown > 0
                  ? 'Could not ask the carrier.'
                  : checked.checked?.some((c) => c.at_carrier)
                    ? 'Not ours yet.'
                    : 'Not with the carrier yet.'}
            </span>
          )}
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            port.status === 'completed' ? 'bg-green-100 text-green-800'
              : port.status === 'rejected' ? 'bg-red-100 text-red-800'
                : 'bg-amber-100 text-amber-800'
          }`}
          >
            {port.status}
          </span>
          {open && canWrite && (
            <button
              type="button"
              disabled={check.isPending}
              onClick={() => check.mutate()}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {check.isPending ? 'Checking…' : 'Check the carrier'}
            </button>
          )}
        </div>
      </div>

      <p className="mt-1 text-sm text-gray-600">
        {SAYS[port.status] || port.status}
        {/* The carrier's own word for it, verbatim and inline — it was a paragraph of its own. Kept
            because it is the only place a status Verso has no case for can show up at all, and
            folding an unfamiliar one into ours would throw it away. */}
        {port.carrierStatus && (
          <span className="ml-1 text-xs text-gray-500">
            (carrier: <span className="font-mono">{port.carrierStatus}</span>)
          </span>
        )}
      </p>

      {/* WHY THE FOC IS STILL BLANK, when it is. A check that reached the carrier's DID records but
          could not find the port ORDER leaves the FOC empty for a reason nobody could see from this
          card — which is exactly the half-hour Kevin spent on Sloan's port. The check says which
          happened; this repeats it rather than showing a blank and letting it read as "no date
          assigned yet". */}
      {checked?.order_note && (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {checked.order_note}
        </p>
      )}
      {checked?.order && (
        <p className="mt-2 text-xs text-gray-600">
          Carrier order <span className="font-mono">{checked.order.portin_id}</span>
          {' · '}<span className="font-mono">{checked.order.status}</span>
          {checked.order.foc_date && ` · FOC ${checked.order.foc_date}`}
          {/* A date we could not parse is shown as the carrier wrote it. Better their words than our
              blank. */}
          {!checked.order.foc_date && checked.order.foc_date_raw
            && ` · FOC "${checked.order.foc_date_raw}" (unrecognised format)`}
        </p>
      )}

      {/* THE REJECTION REASON IS THE POINT OF RECORDING A REJECTION. */}
      {port.status === 'rejected' && port.rejectReason && (
        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{port.rejectReason}</p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm lg:grid-cols-4">
        {[
          // TWO DATES, TWO FIELDS. Kevin, 2026-08-20: "is 'Port Date' the date the port was
          // requested? Or the FOC date?" — followed by "add the port request date if it is
          // available".
          //
          // It was one field showing whichever existed, `carrierFoc || requestedFoc`, with a small
          // grey "(from the carrier)" or "(requested)" as the only clue. Somebody having to ask is
          // the answer. They are different facts and an operator wants both: how long this has been
          // dragging, and when it will actually happen.
          ['Requested', datePart(port.submittedAt || port.createdAt), null],
          // The carrier's commitment. Our requested date is NOT shown here as if it were one — it is
          // a wish until they say otherwise.
          //
          // It used to say this "arrives on its own from their DID record". It does not — that field
          // is null for the whole life of the port. Kevin, 2026-08-25: "I received an email that the
          // port for Sloan has an FOC date… clicked 'Check the carrier', but the FOC date is not
          // updating." The FOC is on the port ORDER, which the check now reads as well.
          ['FOC date', port.carrierFoc || null,
            !port.carrierFoc && port.requestedFoc ? `asked for ${port.requestedFoc}` : null],
          // CALLED WHAT KEVIN CALLS IT. This was "Number in use meanwhile" — labelled by what it
          // is doing, which read as prose rather than as a field. Kevin, 2026-08-20: "the temp
          // number is missing from the porting card." It was not missing; it was under a name he
          // had no reason to scan for. He has said "temp did" and "temp number" every time.
          ['Temp number', port.tempE164 ? fmt(port.tempE164) : null, null],
          ['Old carrier', port.losingCarrier, null],
          // TWO IDS, AND THEY ARE NOT INTERCHANGEABLE. The port-in process id is what the carrier's
          // API takes; the support ticket is what a human quotes to carrier support. This field held
          // the ticket for five days while claiming to be the port id, and every port-in endpoint
          // answers "Port in process 645943 does not exist" to it.
          ['Commio port ID', port.commioPortinId, null],
          ['Carrier ticket', port.carrierTicket, null],
          // Shown because it is the field ports get refused over, and an operator chasing a
          // rejection should not have to go looking for it.
          ['Account · PIN',
            [port.accountNumber, port.accountPin].filter(Boolean).join(' · ') || null, null],
        ].map(([k, v, note]) => (
          <div key={k}>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{k}</dt>
            <dd className="mt-0.5 text-gray-900">
              {v || <span className="text-gray-400">—</span>}
              {/* Shown with or without a value: "no FOC yet, we asked for the 27th" is exactly the
                  state worth reading, and gating the note on the value hid it. */}
              {note && <span className="ml-1 text-xs text-gray-500">({note})</span>}
            </dd>
          </div>
        ))}
      </dl>

      {/* PUTTING A STAND-IN IN SERVICE — one control, while there is nothing working. */}
      {open && !port.tempDidId && !line.number && canWrite && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Lend a spare number until it lands:</span>
          <select
            aria-label="Stand-in number"
            defaultValue=""
            onChange={(e) => { if (e.target.value) assignTemp.mutate(e.target.value); }}
            disabled={assignTemp.isPending || inventory.length === 0}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">{assignTemp.isPending ? 'Putting it in service…' : 'Pick one…'}</option>
            {inventory.map((d) => (
              <option key={d.id} value={d.id}>{fmt(d.e164)}</option>
            ))}
          </select>
          {inventory.length === 0 && !invQ.isLoading && (
            <span className="text-xs text-amber-800">Nothing spare in inventory.</span>
          )}
          {assignTemp.isError && (
            <span className="text-xs text-red-600">
              {assignTemp.error?.body?.error || assignTemp.error?.message}
            </span>
          )}
        </div>
      )}

      {/* CUTOVER DAY.
          Kevin, 2026-08-25, with Sloan's FOC two days out: "build the port-completion flow." What
          this replaces is four manual steps done in order, under time pressure, on the morning the
          customer's number moves.

          Offered while the port is still open and there is a line to put the number on. The API
          verifies at the carrier every time and refuses if the number has not landed — so this
          button is safe to press on the wrong day, which is the day somebody will press it. */}
      {open && port.lineId && canWrite && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          {!plan && (
            <button
              type="button"
              disabled={preview.isPending}
              onClick={() => { setDone(null); preview.mutate(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {preview.isPending ? 'Asking the carrier…' : 'Complete the port…'}
            </button>
          )}

          {/* THE REFUSAL IS THE COMMON CASE and it carries the carrier's own words. "Not landed yet"
              with no reason is what sends somebody to the Commio portal to find out. */}
          {preview.isError && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p>{preview.error?.body?.error || preview.error?.message}</p>
              {(preview.error?.body?.blockers ?? []).map((b) => (
                <p key={b.e164 + b.code} className="mt-1 text-xs">
                  {fmt(b.e164)}: {b.detail}
                </p>
              ))}
            </div>
          )}

          {plan && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">
                The carrier says {port.numbers.map((n) => fmt(n.e164)).join(', ')} is ours. This will:
              </p>
              <ol className="mt-2 space-y-1 text-sm text-gray-700">
                {(plan.would ?? []).map((w) => (
                  <li key={w.step} className={w.manual ? 'text-gray-500' : ''}>
                    · {w.detail}
                    {/* The last line of the plan is not a step this does — it is what is left. Greyed
                        and labelled, because a plan that lists somebody else's work as its own is
                        how the 911 registration gets left pointing at the stand-in. */}
                    {w.manual && <span className="ml-1 text-xs">(not done by this)</span>}
                  </li>
                ))}
              </ol>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  disabled={cutover.isPending}
                  onClick={() => cutover.mutate()}
                  className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {cutover.isPending ? 'Moving it over…' : 'Do it'}
                </button>
                <button
                  type="button"
                  onClick={() => setPlan(null)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Not now
                </button>
              </div>
              {cutover.isError && (
                <p className="mt-2 text-sm text-red-700">
                  {cutover.error?.body?.error || cutover.error?.message}
                </p>
              )}
            </div>
          )}

          {/* AND IT IS NOT FINISHED. The number is on the line in Verso; the carrier is still
              delivering it wherever it was, and 911 still points at the stand-in until a build runs.
              No second Build button here — the action bar is the single place to apply (Kevin,
              2026-08-15, when the duplicate was removed) and the refresh above is what makes this
              show up there as a pending change. */}
          {done && (
            <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-900">
              <p>
                {done.assigned?.map((a) => fmt(a.e164)).join(', ')} is on the line
                {done.released_temp ? ` and ${fmt(done.released_temp)} is back in inventory` : ''}.
              </p>
              <p className="mt-1 text-xs">
                Still to do at the carrier and the PBX: 911 for the new number, the carrier route and
                the caller ID. Apply them with <strong>Save and Apply</strong> below.
              </p>
            </div>
          )}
        </div>
      )}

      {/* GIVING IT BACK — offered only once the port is done, which is the only time it is safe.
          Kevin, 2026-08-20: "once the port comes back as complete, you can show a button to release
          the temp number back to inventory." Before that, handing it back would take away the only
          number the customer has. */}
      {port.status === 'completed' && port.tempDidId && canWrite && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          <button
            type="button"
            disabled={releaseTemp.isPending}
            onClick={() => releaseTemp.mutate()}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {releaseTemp.isPending ? 'Returning…' : `Release ${fmt(port.tempE164)} to inventory`}
          </button>
          {/* The one case that still needs saying: the line has not been moved over yet, so this
              would leave it with no number. */}
          {usingTemp && (
            <p className="mt-1 text-xs text-amber-800">
              Put {port.numbers.map((n) => fmt(n.e164)).join(', ')} on the line first — it is still
              using this one.
            </p>
          )}
          {releaseTemp.isError && (
            <p className="mt-1 text-xs text-red-600">
              {releaseTemp.error?.body?.error || releaseTemp.error?.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
